import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { workflowApi } from '../workflows/api'
import { apiRequest, ApiError, errorMessage } from '../api/client'
import type { RunnableListResponseSchema } from '@shared/schema/api'
import type { z } from 'zod'
import { listRuns, getCachedDefinition, purgeSubmission } from './offline/db'
import type { RunRecord } from './offline/db'
import { startSyncLoop, subscribeSync, retryFailed, refreshSyncCounts } from './offline/sync'
import type { SyncState } from './offline/sync'
import { findNodeById, getPrimaryField } from '@shared/engine/fields'
import { isAnswered, answerToText } from '@shared/schema/answers'
import { Alert, EmptyState, LoadingScreen, PageHeader } from '../ui'

type Runnable = z.infer<typeof RunnableListResponseSchema>['items'][number]
type UnfinishedRun = { run: RunRecord; label: string | null }

/**
 * ดึงคำตอบของฟิลด์แรกที่ตอบไปแล้ว (เช่น "ชื่อร้าน" ในขั้นตอนแรกของ flow) มาแสดง
 * เพื่อให้แยกงานที่ทำค้างไว้หลายชิ้นของแบบสอบถามเดียวกันออกจากกันได้ ไม่ใช่เห็นแต่ชื่อ
 * แบบสอบถามซ้ำ ๆ กันทุกใบ
 */
async function describeRun(run: RunRecord): Promise<string | null> {
  const firstStep = run.snapshot?.visited[0]
  if (!firstStep) return null

  const cached = await getCachedDefinition(run.versionId)
  if (!cached) return null

  const node = findNodeById(cached.definition, firstStep.nodeId)
  const field = node && getPrimaryField(node)
  if (!field) return null

  const value = run.snapshot?.answersByStepKey[firstStep.stepKey]?.[field.name]
  if (!isAnswered(value)) return null

  return answerToText(value).trim() || null
}

/**
 * งานที่ผู้ดูแลลบทิ้งจากหลังบ้านไปแล้ว (เช่นลบผลตอบกลับ) จะเหลือค้างในเครื่องพนักงานตลอดไป
 * ถ้าไม่เช็คคืนกับเซิร์ฟเวอร์ตรงนี้ — เช็คได้เฉพาะตอนออนไลน์ ถ้าเช็คไม่ได้ (เช่นออฟไลน์) ให้เก็บไว้ก่อน
 */
async function stillExistsOnServer(submissionId: string): Promise<boolean> {
  try {
    await apiRequest(`/api/submissions/${submissionId}`)
    return true
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return false
    return true
  }
}

export function RunListPage() {
  const [items, setItems] = useState<Runnable[] | null>(null)
  const [unfinished, setUnfinished] = useState<UnfinishedRun[]>([])
  const [error, setError] = useState<string | null>(null)
  const [sync, setSync] = useState<SyncState | null>(null)

  useEffect(() => {
    startSyncLoop()
    return subscribeSync(setSync)
  }, [])

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const response = await workflowApi.runnable()
        if (!cancelled) setItems(response.items)
      } catch (err) {
        if (!cancelled) {
          setError(errorMessage(err))
          setItems([])
        }
      }
      const runs = (await listRuns()).filter((run) => run.status === 'in_progress')
      const checked = await Promise.all(
        runs.map(async (run) => {
          if (await stillExistsOnServer(run.submissionId)) return run
          await purgeSubmission(run.submissionId)
          return null
        }),
      )
      const alive = checked.filter((run): run is RunRecord => run !== null)
      if (alive.length < runs.length) void refreshSyncCounts()
      const withLabels = await Promise.all(
        alive.map(async (run) => ({ run, label: await describeRun(run) })),
      )
      if (!cancelled) setUnfinished(withLabels)
    })()
    return () => {
      cancelled = true
    }
  }, [])

  if (!items) return <LoadingScreen />

  return (
    <div>
      <PageHeader title="ทำแบบสอบถาม" subtitle="เลือกแบบสอบถามที่ได้รับมอบหมาย" />

      <div className="space-y-4 p-5">
        {sync && sync.pending > 0 && (
          <Alert variant="warning">
            มีข้อมูล {sync.pending} รายการรอส่งขึ้นระบบ
            {!sync.online && ' — กำลังรอสัญญาณอินเทอร์เน็ต'}
          </Alert>
        )}

        {sync && sync.failed > 0 && (
          <Alert variant="error">
            <div className="flex flex-wrap items-center gap-2">
              <span>ส่งไม่สำเร็จถาวร {sync.failed} รายการ (ข้อมูลยังอยู่ในเครื่อง ไม่หาย)</span>
              <button
                type="button"
                onClick={retryFailed}
                className="rounded-md border border-red-300 bg-white px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-100"
              >
                ลองส่งอีกครั้ง
              </button>
            </div>
          </Alert>
        )}

        {error && <Alert variant="error">{error}</Alert>}

        {unfinished.length > 0 && (
          <section className="space-y-2">
            <h2 className="text-sm font-medium text-slate-500">ทำค้างไว้</h2>
            <div className="grid gap-2 sm:grid-cols-2">
              {unfinished.map(({ run, label }) => (
                <Link
                  key={run.submissionId}
                  to={`/run/${run.workflowId}`}
                  className="rounded-xl border border-amber-300 bg-amber-50 p-4 hover:bg-amber-100"
                >
                  <p className="font-medium text-slate-900">
                    {run.workflowName}
                    {label && <span className="font-normal text-slate-600"> · {label}</span>}
                  </p>
                  <p className="mt-1 text-xs text-slate-600">
                    ตอบไปแล้ว {run.snapshot?.visited.length ?? 0} ขั้นตอน
                  </p>
                </Link>
              ))}
            </div>
          </section>
        )}

        {items.length === 0 ? (
          <EmptyState
            title="ยังไม่มีแบบสอบถามที่ได้รับมอบหมาย"
            description="ติดต่อผู้ดูแลระบบเพื่อขอสิทธิ์เข้าทำแบบสอบถาม"
          />
        ) : (
          <section className="space-y-2">
            <h2 className="text-sm font-medium text-slate-500">พร้อมทำ</h2>
            <div className="grid gap-2 sm:grid-cols-2">
              {items.map((item) => (
                <Link
                  key={item.workflowId}
                  to={`/run/${item.workflowId}`}
                  className="rounded-xl border border-slate-200 bg-white p-4 hover:border-[var(--fs-primary)]"
                >
                  <p className="font-medium text-slate-900">{item.name}</p>
                  <p className="mt-1 text-xs text-slate-500">เวอร์ชัน {item.versionNumber}</p>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  )
}
